<template>
  <Sidebar
    :is-open="isOpen"
    :with-backdrop="isOpen"
    backdrop-class="md:hidden"
    @on-backdrop-click="$emit('onBackdropClick')"
  >
    <template #content>
      <div class="px-3">
        <img
          v-if="!isInIframe"
          :src="logo"
          alt="Organization logo"
          class="ml-4 mt-8 mb-4 w-29 h-29 object-contain"
        />

        <SidebarOption
          v-for="(option, optionIndex) in options"
          :key="optionIndex"
          :label="option.label"
          :icon="option.icon"
          :to="option.to"
          :sub-options="option.subOptions"
        />
      </div>
    </template>
    <template #sidebar-bottom>
      <SidebarBottom />
    </template>
  </Sidebar>
</template>

<script setup lang="ts">
import { IconName } from '@exfluential/ui';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { Sidebar } from '~/layouts/_components/sidebar';
import { useOrganizationStore } from '~/store/organization';
import ExfluentialLogoSrc from '~/assets/images/exfluential-logo.svg?url';
import { computed, useOrganizationDomain, useRuntimeConfig } from '#imports';
import { SidebarBottom } from './_components/sidebar-bottom';
import SidebarOption from '~/layouts/_components/left-sidebar/_components/sidebar-option/sidebar-option.vue';
import { useAccountStore } from '~/store/account';
import { useIsInIframe } from '~/composables/use-is-in-iframe';

type Props = {
  isOpen: boolean;
};

defineProps<Props>();
const emit = defineEmits<{
  toggle: [];
  onBackdropClick: [];
}>();

const config = useRuntimeConfig();

const { t } = useI18n();
const organizationDomain = useOrganizationDomain();
const organizationStore = useOrganizationStore();
const { organization } = storeToRefs(organizationStore);
const { currentAccount } = storeToRefs(useAccountStore());
const isInIframe = useIsInIframe();

const logo = computed<string>(() => {
  if (!organizationDomain) {
    return ExfluentialLogoSrc;
  }

  if (organization.value) {
    return organization.value.logoUrl;
  }

  return ExfluentialLogoSrc;
});

const options = computed(() => [
  { icon: IconName.DASHBOARD, label: t('layout.dashboard'), to: '/' },
  {
    icon: IconName.GALLERY_ADD,
    label: t('layout.content'),
    to: '/content',
    subOptions: [
      {
        label: t('all-content.title'),
        to: '/content/all-content',
      },
      ...(organizationDomain &&
      organization.value?.isContentCustomizationEnabled
        ? [
            {
              label: t('layout.my-customized-content'),
              to: '/content/my-personalized-content',
            },
          ]
        : []),
      ...(isInIframe.value
        ? []
        : [
            {
              label: t('layout.my-posts'),
              to: '/content/my-posts',
            },
          ]),
    ],
  },
  ...(organization.value?.isPrintEnabled
    ? [{ icon: IconName.STORE, label: t('layout.print'), to: '/store' }]
    : []),
  ...(organization.value?.isLearningEnabled
    ? [
        {
          icon: IconName.LEARNING,
          label: t('layout.learning'),
          to: '/learning',
        },
      ]
    : []),

  ...(organization.value?.isPlanningEnabled
    ? [
        {
          icon: IconName.PLANNING,
          label: t('layout.planning'),
          subOptions: [
            {
              label: t('layout.calendar'),
              to: '/planning',
            },
          ],
        },
      ]
    : []),
  ...(organization.value?.isPromoteEnabled &&
  currentAccount.value?.currentlySelectedBusiness
    ? [
        {
          icon: IconName.PROMOTE,
          label: t('layout.promote'),
          subOptions: [
            {
              label: t('layout.promote'),
              to: '/promote',
            },
            {
              label: t('layout.budgets'),
              to: '/budgets',
            },
          ],
        },
      ]
    : []),
]);
</script>
